import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import OpportunityRequestsTable from './Components/OpportunityRequestsTable';
import OpportunityRequestsFilterBar from './Components/OpportunityRequestsFilterBar';
import useOpportunityRequestsList from './Hooks/useOpportunityRequestsList';
import type { OpportunityRequestPatchedI } from 'Api/axios/evaluation-opportunity-requests-api';
import type { OpportunityRequestsQueryI } from 'types';

interface Props {
  courseId: string;
}

/**
 * Centralized tab page for reviewing opportunity requests in a course.
 * Shows a filterable, paginated table. Admin-only (permission enforced by useTab).
 */
export default function EditCourseOpportunityRequests({ courseId }: Props) {
  const [statusFilter, setStatusFilter] = useState<OpportunityRequestsQueryI['status'] | ''>('');
  const [page, setPage] = useState(1);

  const { data, pagination, loading, error, fetch } = useOpportunityRequestsList(courseId, {
    status: statusFilter || undefined,
    page,
    pageSize: 20,
  });

  useEffect(() => {
    fetch();
  }, [courseId, statusFilter, page]);

  const handleActionSuccess = (_patched: OpportunityRequestPatchedI, _rowId: string) => {
    fetch();
  };

  const handleFilterChange = (status: OpportunityRequestsQueryI['status'] | '') => {
    setStatusFilter(status);
    setPage(1);
  };

  return (
    <Box>
      <OpportunityRequestsFilterBar value={statusFilter} onChange={handleFilterChange} />

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && <Typography color="error.main">{error}</Typography>}

      {!loading && !error && <OpportunityRequestsTable rows={data ?? []} onActionSuccess={handleActionSuccess} />}

      {pagination && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pagination.totalPages} page={page} onChange={(_e, newPage) => setPage(newPage)} />
        </Box>
      )}
    </Box>
  );
}
